
import {
  defineComponent,
  reactive,
  toRefs,
  computed,
  onMounted,
  PropType,
  Ref
} from "vue";
import { useAuth } from "../../../modules/auth";
import { useToast } from "../../../modules/message";
import { useStorage } from "../../../modules/storage";
import { ClientePayload } from "../../../modules/business/cliente/clienteBusiness";
import BuscaPessoaAntt from "../pessoa/BuscaPessoaAntt.vue";
import {
  PessoaAnttPayload,
  usePessoaAnttService
} from "../../../modules/business/rntrc/pessoa/pessoaAnttBusiness";
import {
  VeiculoAnttPayload,
  safeVeiculoAntt,
  useVeiculoAnttService
} from "../../../modules/business/rntrc/veiculo/veiculoAnttBusiness";
import {
  SERVICOS_VEICULO_RNTRC,
  formatCpfCnpj,
  formatPlaca,
  isCpfCnpjValido,
  isPlacaValida,
  isRenavamValido,
  normalizarPlaca,
  somenteDigitos,
  ServicoVeiculoRntrc
} from "../../../modules/business/rntrc/rntrcTypes";

interface CadastroVeiculoAnttState {
  entity: VeiculoAnttPayload;
  stateReady: boolean;
  savedEntity: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
  placaPreenchida: string;
  documentoPreenchido: string;
  /** Lupa do proprietário: busca na base do cliente. */
  buscaProprietarioAberta: boolean;
  termoBuscaProprietario: string;
  /** Renavam e/ou tipo vieram da consulta à ANTT (extrato), não de um cadastro. */
  preenchidoDaConsulta: boolean;
  /** Lendo o CRLV em PDF escolhido. */
  lendoCrlv: boolean;
  /** Placa, renavam e tipo vieram da leitura do CRLV. */
  lidoDoCrlv: boolean;
  /** O que a leitura do CRLV trouxe: vale sobre o cadastro (é o documento anexado). */
  crlvLido: VeiculoAnttPayload | null;
}

/**
 * Cadastro simplificado de veículo da ANTT: CRLV, placa, renavam, tipo e proprietário. O CRLV em
 * PDF preenche placa, renavam e tipo.
 * Ao informar a placa, traz o veículo já cadastrado na ANTT ou o que a GovPass tem dele (do
 * próprio cliente); grava só no cadastro da ANTT, e o CRLV da GovPass é copiado.
 */
export default defineComponent({
  setup(props, { emit }) {
    const state = reactive<CadastroVeiculoAnttState>({
      entity: safeVeiculoAntt(),
      stateReady: false,
      savedEntity: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: [],
      placaPreenchida: "",
      documentoPreenchido: "",
      buscaProprietarioAberta: false,
      termoBuscaProprietario: "",
      preenchidoDaConsulta: false,
      lendoCrlv: false,
      lidoDoCrlv: false,
      crlvLido: null
    });

    const stateLoading = computed(
      () => state.loadings.length !== 0 || !!props.parentLoading
    );

    const stateBlocked = computed(
      () =>
        stateLoading.value ||
        (!!state.entity.permissionInfo &&
          !useAuth().amIAllowed("edit", state.entity.permissionInfo))
    );

    const placaInvalida = computed(
      () =>
        normalizarPlaca(state.entity.placa).length >= 7 &&
        !isPlacaValida(state.entity.placa)
    );

    const proprietarioInvalido = computed(
      () =>
        somenteDigitos(state.entity.proprietario?.cpfCnpj).length >= 11 &&
        !isCpfCnpjValido(state.entity.proprietario?.cpfCnpj)
    );

    const avisoOrigem = computed(() => {
      switch (state.entity.origem) {
        case "ANT":
          return "Esta placa já está no cadastro da ANTT: ao salvar, ele é atualizado.";
        case "GOV":
          return "Dados trazidos do cadastro de veículos da GovPass, que não é alterado. Confira e salve.";
        default:
          return state.preenchidoDaConsulta
            ? "Renavam e tipo vieram da consulta na ANTT. Informe o proprietário, anexe o CRLV e salve."
            : "";
      }
    });

    const closeScreen = () => {
      emit(
        "after-register-screen-closed",
        state.savedEntity ? state.entity : undefined
      );
    };

    const removeLoading = (loading: Ref<boolean>) => {
      const idx = state.loadings.indexOf(loading);
      if (idx >= 0) {
        state.loadings.splice(idx, 1);
      }
    };

    const setEntity = (entity?: VeiculoAnttPayload | null) => {
      state.entity = safeVeiculoAntt(entity);
      state.entity.placa = formatPlaca(state.entity.placa);
      if (state.entity.proprietario) {
        state.entity.proprietario.cpfCnpj = formatCpfCnpj(
          state.entity.proprietario.cpfCnpj
        );
      }
    };

    /**
     * O que veio da consulta (renavam e tipo do extrato) completa o que o cadastro não trouxe, só
     * para a placa inicial: trocou a placa, não vale mais.
     */
    const completarComConsulta = () => {
      if (
        normalizarPlaca(state.entity.placa) !==
        normalizarPlaca(props.placaInicial)
      ) {
        return;
      }
      const renavam = somenteDigitos(props.renavamInicial);
      const tipo = props.flServicoVeiculoInicial as ServicoVeiculoRntrc;
      if (!somenteDigitos(state.entity.renavam) && renavam) {
        state.entity.renavam = renavam;
        state.preenchidoDaConsulta = true;
      }
      if (!state.entity.flServicoVeiculo && tipo) {
        state.entity.flServicoVeiculo = tipo;
        state.preenchidoDaConsulta = true;
      }
    };

    /** Renavam e tipo lidos do CRLV valem sobre o que o cadastro trouxe. */
    const completarComCrlv = () => {
      const lido = state.crlvLido;
      if (!lido) {
        return;
      }
      if (lido.renavam) {
        state.entity.renavam = lido.renavam;
      }
      if (lido.flServicoVeiculo) {
        state.entity.flServicoVeiculo = lido.flServicoVeiculo;
      }
      // o proprietário do CRLV (no arrendamento, o arrendante) vale sobre o do cadastro
      const dono = lido.proprietario;
      const p = state.entity.proprietario as PessoaAnttPayload;
      if (p && dono?.cpfCnpj) {
        p.cpfCnpj = formatCpfCnpj(dono.cpfCnpj);
        // já preenchido: o blur do campo não troca o nome do CRLV pelo do cadastro
        state.documentoPreenchido = somenteDigitos(dono.cpfCnpj);
      }
      if (p && dono?.nome) {
        p.nome = dono.nome;
      }
    };

    /** Placa informada: o veículo da ANTT, ou o que a GovPass tem dele. */
    const preencherPelaPlaca = () => {
      const placa = normalizarPlaca(state.entity.placa);
      if (
        state.entity.idVeiculoAntt ||
        !isPlacaValida(placa) ||
        placa === state.placaPreenchida
      ) {
        return;
      }
      state.placaPreenchida = placa;
      const { data, loading, preencherVeiculoAntt } = useVeiculoAnttService();
      state.loadings.push(loading);
      preencherVeiculoAntt(placa, props.cliente?.idCliente ?? null)
        .then(() => {
          removeLoading(loading);
          const encontrado = data.value as VeiculoAnttPayload | null;
          if (!encontrado) {
            state.entity.origem = null;
            state.entity.idVeiculoGovpass = null;
            state.entity.possuiCrlvGovpass = null;
            state.entity.nomeProprietarioCrlvGovpass = null;
            completarComConsulta();
            completarComCrlv();
            return;
          }
          setEntity(encontrado);
          completarComConsulta();
          completarComCrlv();
          if (encontrado.origem === "GOV") {
            state.documentoPreenchido = somenteDigitos(
              encontrado.proprietario?.cpfCnpj
            );
          }
        })
        .catch(() => removeLoading(loading));
    };

    /** Lupa: começa pelo que já foi digitado (nome ou parte do documento). */
    const abrirBuscaProprietario = () => {
      const p = state.entity.proprietario as PessoaAnttPayload;
      state.termoBuscaProprietario =
        (p.nome ?? "").trim() || somenteDigitos(p.cpfCnpj);
      state.buscaProprietarioAberta = true;
    };

    const aoEscolherProprietario = (escolhido: PessoaAnttPayload) => {
      state.buscaProprietarioAberta = false;
      const p = state.entity.proprietario as PessoaAnttPayload;
      p.cpfCnpj = formatCpfCnpj(escolhido.cpfCnpj);
      p.nome = escolhido.nome ?? "";
      state.documentoPreenchido = somenteDigitos(escolhido.cpfCnpj);
    };

    /** Documento do proprietário informado: o nome que a ANTT ou a GovPass tem dele. */
    const preencherProprietario = () => {
      const p = state.entity.proprietario as PessoaAnttPayload;
      const documento = somenteDigitos(p.cpfCnpj);
      if (
        !isCpfCnpjValido(documento) ||
        documento === state.documentoPreenchido
      ) {
        return;
      }
      state.documentoPreenchido = documento;
      const { data, loading, preencherPessoaAntt } = usePessoaAnttService();
      state.loadings.push(loading);
      preencherPessoaAntt(documento, props.cliente?.idCliente ?? null)
        .then(() => {
          removeLoading(loading);
          const encontrada = data.value as PessoaAnttPayload | null;
          if (encontrada?.nome) {
            p.nome = encontrada.nome;
          }
        })
        .catch(() => removeLoading(loading));
    };

    const aplicarCrlv = (lido: VeiculoAnttPayload) => {
      const placaLida = normalizarPlaca(lido.placa);
      const placaAtual = normalizarPlaca(state.entity.placa);
      if (state.entity.idVeiculoAntt && placaLida && placaLida !== placaAtual) {
        useToast().warning(
          "Este CRLV é da placa " +
            formatPlaca(placaLida) +
            ", não da " +
            formatPlaca(placaAtual) +
            ". Confira o arquivo."
        );
        return;
      }
      state.crlvLido = lido;
      state.lidoDoCrlv = true;
      if (placaLida && !state.entity.idVeiculoAntt) {
        state.entity.placa = formatPlaca(placaLida);
      }
      completarComCrlv();
      // a placa pode já estar no cadastro da ANTT ou na GovPass: traz o resto (proprietário...)
      preencherPelaPlaca();
      if (!lido.flServicoVeiculo) {
        useToast().info(
          "O CRLV não diz se o veículo é de tração ou tracionado: escolha o tipo."
        );
      }
      if (!lido.proprietario?.cpfCnpj || !lido.proprietario?.nome) {
        useToast().info(
          "O proprietário não foi lido por completo no CRLV: confira o CPF/CNPJ e o nome."
        );
      }
    };

    /**
     * CRLV escolhido: em PDF, a API lê placa, renavam e tipo e a tela se preenche (e busca o
     * cadastro da placa, se houver). Em imagem não há leitura: o usuário preenche à mão.
     */
    const aoEscolherCrlv = () => {
      const campo = document.querySelector(
        "#" + state.stateId + "_crlv"
      ) as HTMLInputElement | null;
      const arquivo =
        campo && campo.files && campo.files.length ? campo.files[0] : null;
      state.lidoDoCrlv = false;
      state.crlvLido = null;
      if (!arquivo) {
        return;
      }
      const ehPdf =
        arquivo.type === "application/pdf" || /\.pdf$/i.test(arquivo.name);
      if (!ehPdf) {
        useToast().info(
          "CRLV em imagem: a leitura automática é só para PDF. Preencha placa, renavam e tipo à mão."
        );
        return;
      }
      const formData = new FormData();
      formData.append("file", arquivo);
      const { data, lerCrlv } = useVeiculoAnttService();
      state.lendoCrlv = true;
      lerCrlv(formData)
        .then(() => aplicarCrlv(data.value as VeiculoAnttPayload))
        .catch(() => undefined)
        .finally(() => (state.lendoCrlv = false));
    };

    const baixarCrlv = () => {
      if (state.entity.idVeiculoAntt) {
        useVeiculoAnttService().abrirCrlv(state.entity.idVeiculoAntt, true);
      }
    };

    const save = () => {
      const service = useVeiculoAnttService();
      state.loadings.push(service.loading);
      const proprietario = state.entity.proprietario as PessoaAnttPayload;
      const payload: VeiculoAnttPayload = {
        ...state.entity,
        placa: normalizarPlaca(state.entity.placa),
        renavam: somenteDigitos(state.entity.renavam) || null,
        chassi: (state.entity.chassi ?? "").trim() || null,
        proprietario: {
          cpfCnpj: somenteDigitos(proprietario.cpfCnpj),
          nome: (proprietario.nome ?? "").trim()
        },
        cliente: props.cliente?.idCliente
          ? ({ idCliente: props.cliente.idCliente } as ClientePayload)
          : state.entity.cliente ?? null,
        origem: null
      };
      // Sem upload, o CRLV viria da GovPass: se o arquivo não existir lá, o veículo é salvo sem.
      const esperavaCrlvGovpass =
        !state.entity.possuiCrlv &&
        !state.entity.keyBlCrlvTemp &&
        !!state.entity.possuiCrlvGovpass;
      (state.entity.idVeiculoAntt
        ? service.updateVeiculoAntt(state.entity.idVeiculoAntt, payload)
        : service.saveNewVeiculoAntt(payload)
      )
        .then(() => {
          removeLoading(service.loading);
          setEntity(service.data.value as VeiculoAnttPayload);
          state.savedEntity = true;
          if (esperavaCrlvGovpass && !state.entity.possuiCrlv) {
            useToast().warning(
              "Veículo salvo, mas o arquivo do CRLV não foi encontrado na GovPass. Anexe o CRLV pelo lápis do veículo."
            );
          } else {
            useToast().success("Veículo salvo no cadastro da ANTT.");
          }
          closeScreen();
        })
        .catch(() => {
          removeLoading(service.loading);
          if (state.entity.keyBlCrlvTemp) {
            useStorage().deleteTempFile(state.entity.keyBlCrlvTemp);
            state.entity.keyBlCrlvTemp = null;
          }
        });
    };

    const validar = (): string => {
      const e = state.entity;
      if (!isPlacaValida(e.placa)) return "Informe uma placa válida.";
      if (!isRenavamValido(e.renavam))
        return "Informe o renavam com 11 dígitos.";
      if (!e.flServicoVeiculo)
        return "Informe se o veículo é de tração ou tracionado.";
      if (!isCpfCnpjValido(e.proprietario?.cpfCnpj)) {
        return "Informe um CPF/CNPJ válido para o proprietário.";
      }
      if (!(e.proprietario?.nome ?? "").trim()) {
        return "Informe o nome do proprietário.";
      }
      return "";
    };

    const submitSave = () => {
      const erro = validar();
      if (erro) {
        useToast().warning(erro);
        return;
      }
      const arquivo = document.querySelector(
        "#" + state.stateId + "_crlv"
      ) as HTMLInputElement;
      if (!arquivo || !arquivo.files || arquivo.files.length === 0) {
        save();
        return;
      }
      const formData = new FormData();
      formData.append("file", arquivo.files[0]);
      const storage = useStorage();
      state.loadings.push(storage.loading);
      storage
        .uploadTempFile(formData)
        .then((key: string) => {
          removeLoading(storage.loading);
          state.entity.keyBlCrlvTemp = key;
          save();
        })
        .catch(() => removeLoading(storage.loading));
    };

    onMounted(() => {
      const service = useVeiculoAnttService();
      state.loadings.push(service.loading);
      (props.idVeiculoAntt
        ? service.getVeiculoAntt(props.idVeiculoAntt)
        : service.newVeiculoAntt(props.cliente?.idCliente ?? null)
      )
        .then(() => {
          removeLoading(service.loading);
          setEntity(service.data.value as VeiculoAnttPayload);
          state.stateReady = true;
          if (!props.idVeiculoAntt && props.placaInicial) {
            state.entity.placa = formatPlaca(
              normalizarPlaca(props.placaInicial)
            );
            preencherPelaPlaca();
          }
        })
        .catch(() => {
          removeLoading(service.loading);
          closeScreen();
        });
    });

    return {
      stateLoading,
      stateBlocked,
      placaInvalida,
      proprietarioInvalido,
      avisoOrigem,
      tiposVeiculo: SERVICOS_VEICULO_RNTRC,
      preencherPelaPlaca,
      preencherProprietario,
      aoEscolherCrlv,
      abrirBuscaProprietario,
      aoEscolherProprietario,
      baixarCrlv,
      submitSave,
      closeScreen,
      ...toRefs(state)
    };
  },
  components: { BuscaPessoaAntt },
  props: {
    idVeiculoAntt: Number,
    cliente: Object as PropType<ClientePayload>,
    placaInicial: String,
    /** Da consulta de veículo (extrato da ANTT): completa o que o cadastro não tiver. */
    renavamInicial: String,
    flServicoVeiculoInicial: String,
    parentLoading: Boolean
  },
  emits: ["after-register-screen-closed"]
});
